/**
 * The change record — every change set the register holds, filtered (bata-ui#166 W2-2, bata-platform
 * ADR 9012, DEC-49, `docs/adr/2007`).
 *
 *   GET /api/v1/registry/changes?state=&author=&family=&parameter_key=&proposed_from=&proposed_to=
 *
 * "Who changed this number, and when, and why" had no answer on screen: the pending queue shows only
 * what waits, and a set leaves it the moment it is decided. Here every set stays readable — proposed,
 * approved, promoted, and since W2-1 `rejected` and `withdrawn` with who closed it, when and for what
 * governed reason. The filters are the platform's and narrow the read itself (never a client-side
 * filter over one page): state, author, family (a set touching the family on any item, DEC-26's
 * rule), parameter key, and the proposal window, inclusive. Every row is exported as CSV.
 *
 * **The filters are on the link** (usability audit C8, 2026-10-02): `?ch.state=&ch.author=&ch.family=
 * &ch.key=&ch.from=&ch.to=`, written with `replaceState`, so a filtered record survives a reload and
 * can be shared; the file states them. The same controls — without the state — filter «Approvazioni
 * in attesa» (`?ap.…`), over the same read with `state=proposed` (`ChangeFilterFields`).
 *
 * **One line per row** (client rule 2, `docs/adr/9028`). The rationale, the closure and the full key
 * list are a set's detail, not columns: they sit behind the info control beside its state, and the
 * CSV still carries the rationale and the closure on every row, since a file has no popover.
 */

import { useCallback, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useApiCollection } from '@/api';
import { listChanges, type ChangeSetSummary, type ListChangesParams } from '@/api/generated';
import { formatDate, formatDateTime } from '@/lib/format';
import { Button, DatePicker, IconButton, Input, Popover, Select, StatusIndicator, type StatusLevel } from '@/primitives';
import type { ProvenanceFilter } from '@/export';
import {
  AsyncRegion, DataTable, IdTag, PersonName, datedName, hasParameterName, parameterName, PeopleIdTags, peopleTexts, personText, tableRequest, type DataColumn,
} from '@/planning';

import { familyKey } from './approval-rule';
import { calendarItemName, parseCalendarTarget } from './process-calendar/calendar-items';
import { principalNames, usePrincipalName } from './change-labels';

/** The states the register publishes, in its order, and each one's words. */
export const CHANGE_STATES = ['proposed', 'approved', 'promoted', 'rejected', 'withdrawn'] as const;

const STATE_KEY: Readonly<Record<string, string>> = {
  proposed: 'foundations.approvals.state.proposed',
  approved: 'foundations.approvals.state.approved',
  promoted: 'foundations.approvals.state.promoted',
  rejected: 'foundations.approvals.state.rejected',
  withdrawn: 'foundations.approvals.state.withdrawn',
};

const STATE_LEVEL: Readonly<Record<string, StatusLevel>> = {
  proposed: 'warning',
  approved: 'good',
  promoted: 'good',
  // A closed set is neither a success nor a fault: it will never take effect, and says why.
  rejected: 'serious',
  withdrawn: 'serious',
};

export function changeStateKey(state: string): string {
  return STATE_KEY[state] ?? 'foundations.approvals.state.unknown';
}

export function changeStateLevel(state: string): StatusLevel {
  return STATE_LEVEL[state] ?? 'serious';
}

/** The closing act's words (the closure view's `outcome`). */
const OUTCOME_KEY: Readonly<Record<string, string>> = {
  reject: 'foundations.changeRecord.closure.reject',
  withdraw: 'foundations.changeRecord.closure.withdraw',
};

export interface ChangeFilters {
  readonly state: string;
  readonly author: string;
  readonly family: string;
  readonly parameterKey: string;
  readonly proposedFrom: string;
  readonly proposedTo: string;
}

export const NO_FILTERS: ChangeFilters = { state: '', author: '', family: '', parameterKey: '', proposedFrom: '', proposedTo: '' };

/** Each filter's URL key under a prefix (`ch` for the record, `ap` for the approvals queue). */
const FILTER_KEYS: Readonly<Record<keyof ChangeFilters, string>> = {
  state: 'state', author: 'author', family: 'family', parameterKey: 'key', proposedFrom: 'from', proposedTo: 'to',
};

/** The filters a link names under `prefix`. Pure. */
export function readChangeFilters(search: string, prefix: string): ChangeFilters {
  const params = new URLSearchParams(search);
  const read = (field: keyof ChangeFilters): string => params.get(`${prefix}.${FILTER_KEYS[field]}`) ?? '';
  return {
    state: read('state'), author: read('author'), family: read('family'), parameterKey: read('parameterKey'),
    proposedFrom: read('proposedFrom'), proposedTo: read('proposedTo'),
  };
}

/** `search` with the filters written under `prefix` (a blank one removed), every other key kept. Pure. */
export function writeChangeFilters(search: string, prefix: string, filters: ChangeFilters): string {
  const params = new URLSearchParams(search);
  for (const field of Object.keys(FILTER_KEYS) as (keyof ChangeFilters)[]) {
    const key = `${prefix}.${FILTER_KEYS[field]}`;
    if (filters[field].trim() === '') params.delete(key);
    else params.set(key, filters[field]);
  }
  const next = params.toString();
  return next === '' ? '' : `?${next}`;
}

/** Whether any filter is stated. Pure. */
export function hasChangeFilters(filters: ChangeFilters): boolean {
  return Object.keys(changeQuery(filters)).length > 0;
}

/** The filters as the export's provenance states them — the read's own parameter names. Pure. */
export function changeProvenance(filters: ChangeFilters): readonly ProvenanceFilter[] {
  return Object.entries(changeQuery(filters)).map(([field, value]) => ({ field, operator: 'equals', value: String(value) }));
}

/** The filters held on the link under `prefix`: read once, written with `replaceState` on every change. */
export function useChangeFilters(prefix: string): readonly [ChangeFilters, (next: ChangeFilters) => void] {
  const [filters, setFilters] = useState<ChangeFilters>(
    () => (typeof window === 'undefined' ? NO_FILTERS : readChangeFilters(window.location.search, prefix)),
  );
  const set = useCallback((next: ChangeFilters): void => {
    setFilters(next);
    if (typeof window === 'undefined') return;
    window.history.replaceState(
      window.history.state, '', `${window.location.pathname}${writeChangeFilters(window.location.search, prefix, next)}${window.location.hash}`,
    );
  }, [prefix]);
  return [filters, set];
}

/**
 * Author, family, parameter key and the proposal window — the read's own filters, shared by the
 * record and the approvals queue (which reads `state=proposed` and so draws no state control).
 */
export function ChangeFilterFields({ filters, onChange }: {
  readonly filters: ChangeFilters;
  readonly onChange: (fields: Partial<ChangeFilters>) => void;
}): React.JSX.Element {
  return (
    <>
      <Input labelKey="foundations.changeRecord.filter.author" value={filters.author} onChange={(event) => onChange({ author: event.target.value })} />
      <Input
        labelKey="foundations.changeRecord.filter.family"
        helpKey="foundations.changeRecord.filter.familyHelp"
        value={filters.family}
        onChange={(event) => onChange({ family: event.target.value })}
      />
      <Input labelKey="foundations.changeRecord.filter.parameterKey" value={filters.parameterKey} onChange={(event) => onChange({ parameterKey: event.target.value })} />
      <DatePicker
        labelKey="foundations.changeRecord.filter.from"
        {...(filters.proposedFrom === '' ? {} : { value: filters.proposedFrom })}
        onValueChange={(iso) => onChange({ proposedFrom: iso })}
      />
      <DatePicker
        labelKey="foundations.changeRecord.filter.to"
        {...(filters.proposedTo === '' ? {} : { value: filters.proposedTo })}
        onValueChange={(iso) => onChange({ proposedTo: iso })}
      />
    </>
  );
}

/** The read's query: only the filters stated, trimmed — a blank filter is no filter, never `""`. */
export function changeQuery(filters: ChangeFilters): NonNullable<ListChangesParams['query']> {
  const query: Record<string, string> = {};
  const put = (name: string, value: string): void => { if (value.trim() !== '') query[name] = value.trim(); };
  put('state', filters.state);
  put('author', filters.author);
  put('family', filters.family);
  put('parameter_key', filters.parameterKey);
  put('proposed_from', filters.proposedFrom);
  put('proposed_to', filters.proposedTo);
  return query;
}

/** The set's approvers by name, aligned index by index with `approvedBy` (usability audit B6). */
function approversOf(change: ChangeSetSummary, t: TFunction): string[] {
  return peopleTexts(t, change.approvedBy, change.approvedByNames, undefined, change.approvedByNamesAbsentReasons);
}

function closureWords(change: ChangeSetSummary, t: TFunction, language: string): string | null {
  const closure = change.closure ?? null;
  if (closure === null) return null;
  return t(OUTCOME_KEY[closure.outcome] ?? 'foundations.changeRecord.closure.other', {
    by: personText(t, closure.closedBy, closure.closedByName, undefined, closure.closedByNameAbsentReason),
    when: formatDateTime(closure.closedAt, language, 'UTC'),
    reason: closure.reasonCode,
    outcome: closure.outcome,
  });
}

/** A set's detail — rationale, closure, every key — behind one control, so its row stays one line. */
function ChangeDetail({ change }: { readonly change: ChangeSetSummary }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const closure = closureWords(change, t, i18n.language);
  const note = change.closure?.note ?? '';
  const keys = change.parameterKeys ?? [];
  const label = t('foundations.changeRecord.detail', { changeId: change.changeId });
  return (
    <Popover
      triggerLabelKey="foundations.changeRecord.detail"
      align="start"
      trigger={<IconButton variant="ghost" size="sm" icon={<Info size={14} aria-hidden />} aria-label={label} title={label} />}
    >
      <dl className="text-caption flex max-w-md flex-col gap-2" data-change-detail={change.changeId}>
        {closure !== null && (
          <div data-closure={change.closure?.outcome}>
            <dt className="sr-only">{t('foundations.changeRecord.column.closure')}</dt>
            <dd>{closure}</dd>
            {note !== '' && <dd className="text-ink-secondary">{t('foundations.changeRecord.closure.note', { note })}</dd>}
          </div>
        )}
        <div>
          <dt className="text-ink-secondary">{t('foundations.changeRecord.column.rationale')}</dt>
          <dd className="[overflow-wrap:anywhere]">{change.rationale}</dd>
        </div>
        {keys.length > 0 && (
          <div>
            <dt className="text-ink-secondary">{t('foundations.changeRecord.column.keys')}</dt>
            {/* Each parameter by its name, its key one press away in the «id» — the key is what an audit cites (audit A4). */}
            {keys.map((key) => (
              <dd key={key} className="flex flex-wrap items-baseline gap-1" data-change-key={key}>
                {hasKeyName(key)
                  ? <span>{keyName(t, key)}</span>
                  : <span className="font-numeric text-ink-secondary [overflow-wrap:anywhere]" translate="no">{key}</span>}
                {hasKeyName(key) && <IdTag id={key} kind={t('registry.keyKind')} />}
              </dd>
            ))}
          </div>
        )}
      </dl>
    </Popover>
  );
}

const CELL = 'py-1 pe-3 whitespace-nowrap';

/** A key's name: a calendar period's by its market and period (ADR 9068), a parameter's by its card. */
function keyName(t: TFunction, key: string): string {
  const period = parseCalendarTarget(key);
  return period === null ? parameterName(t, key) : calendarItemName(t, period);
}

function hasKeyName(key: string): boolean {
  return parseCalendarTarget(key) !== null || hasParameterName(key);
}

export function ChangeRecordPanel(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useChangeFilters('ch');
  const patch = (fields: Partial<ChangeFilters>): void => setFilters({ ...filters, ...fields });
  const changes = useApiCollection(listChanges, { query: changeQuery(filters) });
  const principalName = usePrincipalName();

  const columns = useMemo((): readonly DataColumn<ChangeSetSummary>[] => {
    const accountOf = (id: string): string => principalName(id, null);
    const approvers = (change: ChangeSetSummary): string[] => approversOf(change, t);
    // A calendar date in the planner's format, never the ISO string (audit F).
    const day = (iso: string): string => (Number.isNaN(Date.parse(iso)) ? iso : formatDate(iso, i18n.language, 'UTC'));
    const families = (change: ChangeSetSummary): string => (change.families ?? [])
      .map((family) => { const key = familyKey(family); return key === null ? family : t(key); })
      .join(', ');
    return [
    {
      id: 'change_id', headerKey: 'foundations.approvals.column.changeId', descriptionKey: 'foundations.help.changeRecord.colChangeId', kind: 'text',
      // The change by the day it was proposed, its id one press away in the «id» (audit A4, 2026-10-10).
      value: (change) => datedName('change', change.proposedOn, t, i18n.language),
      cell: (change) => (
        <th scope="row" className={`${CELL} text-start font-normal`}>
          <span className="inline-flex items-baseline gap-1">
            <span>{datedName('change', change.proposedOn, t, i18n.language)}</span>
            <IdTag id={change.changeId} kind={t('idWords.kindChange')} />
          </span>
        </th>
      ),
    },
    {
      id: 'state', headerKey: 'foundations.approvals.column.state', descriptionKey: 'foundations.help.changeRecord.colState', kind: 'enum',
      value: (change) => change.state,
      label: (state) => t(changeStateKey(state), { state }),
      cell: (change) => (
        <td className={CELL}>
          <span className="inline-flex items-center gap-1">
            <StatusIndicator level={changeStateLevel(change.state)} labelKey={changeStateKey(change.state)} values={{ state: change.state }} />
            <ChangeDetail change={change} />
          </span>
        </td>
      ),
    },
    {
      id: 'families', headerKey: 'foundations.changeRecord.column.families', descriptionKey: 'foundations.help.changeRecord.colFamilies', kind: 'text',
      value: (change) => (change.families ?? []).join(',') || null,
      cell: (change) => <td className={CELL}>{families(change) || t('foundations.changeRecord.noFamily')}</td>,
    },
    {
      id: 'parameter_keys', headerKey: 'foundations.changeRecord.column.keys', descriptionKey: 'foundations.help.changeRecord.colKeys', kind: 'text',
      value: (change) => (change.parameterKeys ?? []).join(',') || null,
      cell: (change) => {
        const keys = change.parameterKeys ?? [];
        return (
          <td className={`${CELL} text-caption`}>
            {/* The parameter by its name where the card has prose for it, an exception rule's key by
                its rule and field (audit A6); its key otherwise. Every key is behind the info control. */}
            {keys.length === 0
              // A sentence, not a value: it wraps rather than being cut (DEC-111 residual D).
              ? <span className="block min-w-[12rem] whitespace-normal">{t('foundations.changeRecord.keysUnpublished')}</span>
              : keys.length === 1 ? keyName(t, keys[0] ?? '')
                : t('foundations.changeRecord.moreKeys', { first: keyName(t, keys[0] ?? ''), count: keys.length - 1 })}
          </td>
        );
      },
    },
    {
      id: 'author', headerKey: 'foundations.approvals.column.author', descriptionKey: 'foundations.help.changeRecord.colAuthor', kind: 'text',
      // By name (usability audit B6): the profile name the read publishes, «Sistema» for the
      // platform, the id as before when no name is on record.
      value: (change) => personText(t, change.author, change.authorName, undefined, change.authorNameAbsentReason),
      // Without a profile name and without a reason, the subject as «account d9ba9aa2», the whole id in its tooltip (DEC-97); with the read's reason, the reason in words and the id behind the «id» button (ADR 2127).
      cell: (change) => <td className={CELL}><PersonName subject={change.author} name={change.authorName} fallback={accountOf} reason={change.authorNameAbsentReason} /></td>,
    },
    {
      id: 'proposed_on', headerKey: 'foundations.approvals.column.proposedOn', descriptionKey: 'foundations.help.changeRecord.colProposedOn', kind: 'date',
      value: (change) => change.proposedOn,
      cell: (change) => <td className={CELL}>{day(change.proposedOn)}</td>,
    },
    {
      id: 'approved', headerKey: 'foundations.changeRecord.column.approved', descriptionKey: 'foundations.help.changeRecord.colApproved', kind: 'text',
      value: (change) => approvers(change).join(', ') || null,
      cell: (change) => {
        // On screen an approver without a profile name reads «account d9ba9aa2» (DEC-97, as the
        // cards do), the whole ids in the tooltip; the file keeps the ids (`value`, `approved_by_ids`).
        const shown = principalNames(principalName, change.approvedBy, change.approvedByNames, change.approvedByNamesAbsentReasons).join(', ');
        return (
          <td className={CELL}>
            {(change.approvedBy ?? []).length === 0
              ? <span className="text-caption text-ink-secondary">{t('foundations.changeRecord.notApproved')}</span>
              : (
                <span title={(change.approvedBy ?? []).join(', ')} data-approved-by="">
                  {/* No approval date is its own sentence, never «da Rossi il » (audit D10). */}
                  {change.approvedOn === null || change.approvedOn === undefined || change.approvedOn === ''
                    ? t('foundations.changeRecord.approvedByNoDate', { approvers: shown })
                    : t('foundations.changeRecord.approvedBy', { approvers: shown, on: day(change.approvedOn) })}
                  <PeopleIdTags subjects={change.approvedBy} names={change.approvedByNames} reasons={change.approvedByNamesAbsentReasons} />
                </span>
              )}
          </td>
        );
      },
    },
    ];
  }, [t, i18n.language, principalName]);

  // The CSV keeps what the row moved behind its info control: a file has no popover.
  const exportColumns = useMemo((): readonly DataColumn<ChangeSetSummary>[] => [
    ...columns,
    {
      id: 'rationale', headerKey: 'foundations.changeRecord.column.rationale', descriptionKey: 'foundations.help.changeRecord.colRationale', kind: 'text',
      value: (change) => change.rationale, cell: () => null,
    },
    {
      id: 'closure', headerKey: 'foundations.changeRecord.column.closure', descriptionKey: 'foundations.help.changeRecord.colClosure', kind: 'text',
      value: (change) => {
        const words = closureWords(change, t, i18n.language);
        const note = change.closure?.note ?? '';
        return words === null ? null : note === '' ? words : t('foundations.changeRecord.closureWithNote', { closure: words, note });
      },
      cell: () => null,
    },
    // The principals' ids beside their names: a name is for reading, the id is what an audit cites.
    {
      id: 'author_id', headerKey: 'foundations.changeRecord.column.authorId', descriptionKey: 'foundations.help.changeRecord.colAuthorId', kind: 'text',
      value: (change) => change.author, cell: () => null,
    },
    {
      id: 'approved_by_ids', headerKey: 'foundations.changeRecord.column.approvedByIds', descriptionKey: 'foundations.help.changeRecord.colApprovedByIds', kind: 'text',
      value: (change) => (change.approvedBy ?? []).join(', ') || null, cell: () => null,
    },
  ], [columns, t, i18n.language]);

  return (
    <div className="flex flex-col gap-3">
      <div data-tour="foundations-parameters-history-filters" className="grid gap-3 md:grid-cols-3">
        <Select
          labelKey="foundations.changeRecord.filter.state"
          options={[
            { value: '__all__', labelKey: 'foundations.changeRecord.filter.allStates' },
            ...CHANGE_STATES.map((state) => ({ value: state, labelKey: changeStateKey(state) })),
          ]}
          value={filters.state === '' ? '__all__' : filters.state}
          onValueChange={(value) => patch({ state: value === '__all__' ? '' : value })}
        />
        <ChangeFilterFields filters={filters} onChange={patch} />
      </div>
      <div>
        <Button variant="ghost" size="sm" onClick={() => setFilters(NO_FILTERS)}>{t('foundations.changeRecord.filter.clear')}</Button>
      </div>
      <AsyncRegion
        query={changes}
        errorTitleKey="foundations.changeRecord.loadFailed"
        emptyTitleKey="foundations.changeRecord.emptyTitle"
        emptyBodyKey="foundations.changeRecord.emptyBody"
        isEmpty={(rows) => rows.length === 0}
        loadingRows={4}
      >
        {(rows) => (
          <DataTable sourceKey="foundations.help.changeRecord.source"
            tourAnchor="foundations-parameters-history-record-table"
            rows={rows}
            rowKey={(change) => change.changeId}
            captionKey="foundations.changeRecord.caption"
            exportRequest={(view) => {
              // The file states the filters that made it (usability audit C8).
              const request = tableRequest({
                id: 'foundations.changeRecord.table', titleKey: 'foundations.changeRecord.title', columns: exportColumns, rows: view, t,
              });
              const stated = changeProvenance(filters);
              return {
                ...request,
                provenance: { ...request.provenance, filters: stated },
                perimeter: { ...request.perimeter, filters: stated },
              };
            }}
            headerRowClassName="border-hairline border-b text-ink-secondary"
            rowProps={() => ({ className: 'border-hairline border-b' })}
            columns={columns}
          />
        )}
      </AsyncRegion>
    </div>
  );
}
